class DynamicPricing {
  constructor() {
    // Wait for theme settings to be available
    if (!window.auroraTheme?.themeSettings) {
      console.warn(
        '[DynamicPricing] Theme settings not available yet, waiting...'
      );
      setTimeout(() => new DynamicPricing(), 100);
      return;
    }

    // Don't initialize if dynamic pricing is disabled
    if (!window.dynamicDiscountEnabled) {
      console.log(
        '[DynamicPricing] Dynamic pricing is disabled, skipping initialization'
      );
      return;
    }

    this.discountPercentage = window.dynamicDiscountPercentage || 0;
    this.discountEnabled = window.dynamicDiscountEnabled || false;
    this.badgeBackgroundColor =
      window.auroraTheme.themeSettings.dynamic_discount_badge_bg || '#000000';
    // Make these values available globally for liquid templates
    window.dynamicDiscountPercentage = this.discountPercentage;
    window.dynamicDiscountEnabled = this.discountEnabled;
    console.log('[DynamicPricing] Initialized with:', {
      percentage: this.discountPercentage,
      enabled: this.discountEnabled,
      windowObj: {
        dynamicDiscountPercentage: window.dynamicDiscountPercentage,
        dynamicDiscountEnabled: window.dynamicDiscountEnabled,
        badgeBackgroundColor: this.badgeBackgroundColor,
        themeSettings: window.auroraTheme.themeSettings,
      },
    });
    this.init();
  }

  init() {
    console.log('[DynamicPricing] Starting initialization');
    // Bind the methods to ensure 'this' context is preserved
    this.updatePrices = this.updatePrices.bind(this);
    this.calculateDiscountedPrice = this.calculateDiscountedPrice.bind(this);
    this.observeDynamicContent = this.observeDynamicContent.bind(this);
    this.handleDynamicContent = this.handleDynamicContent.bind(this);
    this.handleVariantChange = this.handleVariantChange.bind(this);

    if (document.readyState === 'loading') {
      console.log(
        '[DynamicPricing] Document still loading, adding DOMContentLoaded listener'
      );
      document.addEventListener('DOMContentLoaded', () => {
        console.log('[DynamicPricing] DOMContentLoaded fired, updating prices');
        this.updatePrices();
        this.observeDynamicContent();
        this.setupVariantChangeListeners();
      });
    } else {
      console.log(
        '[DynamicPricing] Document already loaded, updating prices immediately'
      );
      this.updatePrices();
      this.observeDynamicContent();
      this.setupVariantChangeListeners();
    }
  }

  setupVariantChangeListeners() {
    // Listen for variant changes on product page
    document.addEventListener(
      'vsk:product:variant:changed',
      this.handleVariantChange
    );

    // Listen for variant changes on collection page
    document.addEventListener('vsk:variant:changed', this.handleVariantChange);

    // Listen for product changes on collection page
    document.addEventListener('vsk:product:changed', this.handleVariantChange);

    // Also listen for clicks on variant picker dropdowns
    document.addEventListener('click', (event) => {
      const variantTrigger = event.target.closest('[data-trigger="click"]');
      if (variantTrigger) {
        // Wait for the variant picker to update the price
        setTimeout(() => {
          console.log(
            '[DynamicPricing] Variant picker clicked, updating prices'
          );
          this.updatePrices();
        }, 100);
      }
    });
  }

  handleVariantChange(event) {
    console.log(
      '[DynamicPricing] Variant change detected:',
      event.type,
      event.detail
    );

    // Clear the original price data attribute so it will be recalculated
    const priceWrappers = document.querySelectorAll(
      '.product-price-wrapper, .product-card__price-cost'
    );
    priceWrappers.forEach((wrapper) => {
      wrapper.removeAttribute('data-original-price');
    });

    // Update prices with a small delay to ensure the variant price is updated
    setTimeout(() => {
      console.log('[DynamicPricing] Updating prices after variant change');
      this.updatePrices();
    }, 100);
  }

  observeDynamicContent() {
    console.log(
      '[DynamicPricing] Setting up MutationObserver for dynamic content'
    );

    // Create a MutationObserver to watch for new products
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.type === 'childList' && mutation.addedNodes.length > 0) {
          this.handleDynamicContent(mutation.addedNodes);
        }
      });
    });

    // Observe the shop products grid for changes
    const productsGrid = document.querySelector('.shop__products-grid');
    if (productsGrid) {
      console.log('[DynamicPricing] Found products grid, starting observation');
      observer.observe(productsGrid, {
        childList: true,
        subtree: true,
      });
    } else {
      console.log('[DynamicPricing] Products grid not found');
    }

    // Also observe product recommendations if they exist
    const productRecommendations = document.querySelector(
      '.product-recommendations'
    );
    if (productRecommendations) {
      console.log(
        '[DynamicPricing] Found product recommendations, starting observation'
      );
      observer.observe(productRecommendations, {
        childList: true,
        subtree: true,
      });
    }
  }

  handleDynamicContent(addedNodes) {
    console.log('[DynamicPricing] Handling dynamic content', {
      nodeCount: addedNodes.length,
    });

    addedNodes.forEach((node) => {
      if (node.nodeType === Node.ELEMENT_NODE) {
        // Check if the added node is a product card or contains product cards
        const priceWrappers = node.querySelectorAll(
          '.product-price-wrapper, .product-card__price-cost'
        );
        if (priceWrappers.length > 0) {
          console.log(
            '[DynamicPricing] Found new price wrappers in dynamic content:',
            priceWrappers.length
          );
          this.updatePrices();
        }
      }
    });
  }

  calculateDiscountedPrice(price) {
    if (!this.discountEnabled || !this.discountPercentage) {
      return price;
    }
    return price * (1 - this.discountPercentage / 100);
  }

  formatMoney(cents) {
    const format =
      window.auroraTheme.themeSettings.moneyFormat || '${{amount}}';
    const value = (cents / 100).toFixed(2);
    return format.replace('{{amount}}', value);
  }

  async fetchCartDiscounts() {
    console.log('[DynamicPricing] Fetching cart discounts');
    try {
      const response = await fetch('/cart.js');
      const cart = await response.json();
      console.log('[DynamicPricing] Cart data:', cart);
      return cart.total_discount;
    } catch (error) {
      console.error('[DynamicPricing] Error fetching cart discounts:', error);
      return 0;
    }
  }

  extractPriceAmount(priceText) {
    // Remove currency symbol and any other non-numeric characters except decimal point
    const numericString = priceText.replace(/[^0-9.]/g, '');
    return parseFloat(numericString) * 100; // Convert to cents
  }

  async updatePrices() {
    console.log('[DynamicPricing] Starting price update process');

    // Update price displays to show both original and discounted prices
    const priceWrappers = document.querySelectorAll(
      '.product-price-wrapper, .product-card__price-cost'
    );
    console.log('[DynamicPricing] Found price wrappers:', {
      count: priceWrappers.length,
      elements: Array.from(priceWrappers).map((el) => ({
        classes: el.className,
        html: el.innerHTML.slice(0, 100) + '...', // First 100 chars for brevity
      })),
    });

    priceWrappers.forEach((wrapper, index) => {
      console.log(`[DynamicPricing] Processing wrapper ${index}:`, {
        classes: wrapper.className,
        id: wrapper.id,
      });

      // Support both product page and collection page class names
      const currentPriceEl = wrapper.querySelector(
        '.product-price__current-price, .product-card__price-item'
      );
      let oldPriceEl = wrapper.querySelector(
        '.product-price__old-price, .product-card__price-item-old'
      );
      const costContainer =
        wrapper.querySelector('.product-price__cost') || wrapper;

      if (!currentPriceEl) {
        console.warn(
          `[DynamicPricing] Wrapper ${index}: Missing current price element`,
          {
            currentPrice: !!currentPriceEl,
          }
        );
        return;
      }

      // Get the original price - either from data attribute or current price
      let originalPriceInCents;
      if (wrapper.hasAttribute('data-original-price')) {
        originalPriceInCents = parseInt(
          wrapper.getAttribute('data-original-price'),
          10
        );
      } else {
        originalPriceInCents = this.extractPriceAmount(
          currentPriceEl.textContent
        );
        // Store the original price for future reference
        wrapper.setAttribute(
          'data-original-price',
          originalPriceInCents.toString()
        );
      }

      console.log(
        `[DynamicPricing] Original price for wrapper ${index}:`,
        originalPriceInCents
      );

      // If dynamic pricing is disabled, restore original theme behavior
      if (!this.discountEnabled) {
        // Remove any dynamic pricing elements we added
        const dynamicDiscountBadge = costContainer.querySelector(
          '.product-badge--dynamic-discount'
        );
        if (dynamicDiscountBadge) {
          dynamicDiscountBadge.remove();
        }

        // Only show old price if the product has a compare_at_price (theme's original behavior)
        const hasCompareAtPrice =
          wrapper.hasAttribute('data-has-compare-price') &&
          wrapper.getAttribute('data-has-compare-price') === 'true';

        if (oldPriceEl) {
          if (!hasCompareAtPrice) {
            oldPriceEl.remove();
          }
        }

        // Restore original price display
        currentPriceEl.textContent = this.formatMoney(originalPriceInCents);
        return;
      }

      // Calculate the discounted price
      const discountedPriceInCents =
        this.calculateDiscountedPrice(originalPriceInCents);
      console.log(
        `[DynamicPricing] Discounted price for wrapper ${index}:`,
        discountedPriceInCents
      );

      // Determine if this is a product card (collection page) or product page
      const isProductCard = currentPriceEl.classList.contains(
        'product-card__price-item'
      );

      // Create or update the old price element
      if (!oldPriceEl) {
        oldPriceEl = document.createElement('div');
        // Use appropriate class name based on context
        oldPriceEl.className = isProductCard
          ? 'product-card__price-item-old'
          : 'product-price__old-price';

        // Add highlight class if needed
        if (
          isProductCard &&
          currentPriceEl.classList.contains(
            'product-card__price-item--highlight-sale'
          )
        ) {
          oldPriceEl.classList.add(
            'product-card__price-item-old--highlight-sale'
          );
        } else if (
          currentPriceEl.classList.contains(
            'product-price__current-price--highlight-sale'
          )
        ) {
          oldPriceEl.classList.add('product-price__old-price--highlight-sale');
        }

        costContainer.insertBefore(oldPriceEl, currentPriceEl);
      }

      // Update the prices
      oldPriceEl.textContent = this.formatMoney(originalPriceInCents);
      currentPriceEl.textContent = this.formatMoney(discountedPriceInCents);

      // Add discount badge if it doesn't exist
      let discountBadge = costContainer.querySelector(
        '.product-badge--dynamic-discount'
      );
      if (
        !discountBadge &&
        this.discountEnabled &&
        this.discountPercentage > 0
      ) {
        discountBadge = document.createElement('div');
        discountBadge.className =
          'product-badge product-badge--dynamic-discount';
        discountBadge.textContent = `Save ${this.discountPercentage}%`;

        // Apply all necessary styles
        Object.assign(discountBadge.style, {
          backgroundColor: this.badgeBackgroundColor,
          color: '#FFFFFF',
          display: 'inline-flex',
          alignItems: 'center',
          padding: '4px 8px',
          borderRadius: '4px',
          marginLeft: '10px',
          order: '3',
          fontWeight: 'var(--gsc-font-weight-normal)',
          fontSize: '14px',
          lineHeight: '1.2',
        });

        costContainer.appendChild(discountBadge);
      }
    });
  }
}

// Export the class for use in other files
if (typeof module !== 'undefined' && module.exports) {
  module.exports = DynamicPricing;
}
